import React, { useEffect, useState } from 'react';
import RouterLink from 'react-router/lib/Link';
import PropTypes from 'prop-types';
import suitcss from '../../../helpers/suitcss';
import { getBrowserInfo, openUrl } from '../../../services/bridge';
import SvgLoader from '../media/MediaSvgLoader';
import { ariaHtmlAttributes, ariaPropsTypes } from '../../../helpers/ariaAttributes';

/**
 * Note: If the `target` attribute is set, React Router performs a full page reload
 * instead of a router redirect. This is desired for external links.
 * If `target` is not set, we set it to '_self'. Why not '_blank'? TODO: @jan zaeske
 * Internal links should be handled by React Router, so the `target` is set to null.
 * PDF links are always handled as external links and opened in a new tab.
 */
export const getTargetType = (to, target) => {
  // pdf links are always opened in a new tab
  if (to && /\.pdf$/.test(to.toLowerCase())) {
    return '_blank';
  }

  // checking if the link is an external url
  const isExternal = to && (
    to.startsWith('http://')
    || to.startsWith('https://')
    || to.startsWith('//')
    || to.startsWith('tel:')
    || target === '_blank'
  );

  // external links without target
  if (isExternal && !target) {
    return '_self';
  }

  // internal links with target _self
  if (!isExternal && target === '_self') {
    return null;
  }

  // default target if none was set
  return target || null;
};

function handleKeyDown(ev, forceSpaceTrigger, to, isDisabled) {
  if (isDisabled) return;
  if (!forceSpaceTrigger) return;
  if (ev.key === 'Enter' || ev.key === ' ') {
    ev.preventDefault();
    window.location.href = to; // eslint-disable-line no-undef
  }
}

const componentName = 'TextLink';

function TextLink(props) {
  const {
    element,
    isActive,
    isDisabled,
    className,
    utilities,
    data,
    href,
    isLoading,
    linkId,
    isLoggedIn,
    to: propTo,
    target: propTarget,
    onClick: propOnClick,
    size,
    color,
    outline,
    icon,
    buttonFilled,
    withoutArrow,
    withoutStyle,
    buttonWithoutLabel,
    tertiary,
    highlight,
    asShareButton,
    asShareButtonInverted,
    standalone,
    asButton,
    noHover,
    forceSpaceTrigger,
    children,
  } = props;

  // only render the component for guests and logged in users on the client side
  // this avoids hydration issues with react-router and tests failing
  // serverside -> href=null VS. client side -> href="/real-url-from-api-call"
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  if (isLoggedIn && !isClient) {
    return null;
  }

  // If the element is disabled and is an "a" tag (or no specific element is provided),
  // it is converted to a "span" tag since "a" tags cannot be disabled in HTML.
  const Element = isDisabled && (!element || element === 'a') ? 'span' : (element || RouterLink);
  const target = getTargetType(propTo, propTarget);
  const states = [
    isActive && 'active',
    isDisabled && 'disabled',
    isLoading && 'loading',
  ];

  // @todo describe what happens here and how this is related to tracking.
  const dataset = Object.keys(data || {}).reduce((result, key) => ({
      ...result,
      [`data-${key}`]: data[key],
    }), {});

  if (element === 'a' && process.browser && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('Prop "element" should not be "a". For "a" tags ommit Prop "element"');
  }

  // set onClick handler
  let onClick = null;
  let to = typeof Element === 'string' && Element !== 'a' ? null : propTo;
  if (!isDisabled) {
    if (propOnClick) {
      onClick = propOnClick;
    } else if (propTo && target === '_blank' && getBrowserInfo().isWebView) {
      onClick = (ev) => { ev.preventDefault(); openUrl(propTo); };
      to = null;
    }
  } else {
    to = undefined;
    onClick = (ev) => { ev.preventDefault(); return false; };
  }

  const modifiers = [
    size,
    color,
    outline,
    icon && 'icon',
    buttonFilled && 'buttonFilled',
    !withoutArrow && !withoutStyle && 'arrow',
    buttonWithoutLabel && 'buttonWithoutLabel',
    tertiary && 'tertiary',
    highlight && 'highlight',
    asShareButton && 'asShareButton',
    asShareButtonInverted && 'asShareButtonInverted',
    standalone && 'standalone',
    withoutStyle && 'noStyle',
    // Always render as a button when 'asButton' is true
    asButton === true && 'asButton',
    // If 'asButton' is set to a breakpoint (e.g., asButton="SM"),
    // it will render as a button only at the specified breakpoint.
    typeof asButton === 'string' && `asButton-${asButton}`,
    noHover && 'noHover',
  ];

  const onKeyDown = (ev) => handleKeyDown(ev, forceSpaceTrigger, to || href, isDisabled);

  return (
    <Element
      id={linkId}
      to={href ? null : to}
      href={href}
      onClick={onClick}
      className={suitcss({
        componentName,
        modifiers,
        states,
        className,
        utilities,
      })}
      {...ariaHtmlAttributes(props)}
      target={target}
      {...dataset}
      onKeyDown={onKeyDown}
    >
      {icon && (
        <span className={suitcss({ componentName, descendantName: 'icon' })}>
          <SvgLoader path={icon} />
        </span>
      )}

      {children && asButton && (
        <span className={suitcss({ componentName, descendantName: 'text' })}>
          {children}
        </span>
      )}

      {children && !asButton && (
        <span className={suitcss({ componentName, descendantName: 'text' })}>
          <span className={suitcss({ componentName, descendantName: 'underline' })}>
            {children}
          </span>
        </span>
      )}

      {isLoading && (
        <span className={suitcss({ componentName, descendantName: 'loadingIcon' })}>
          <SvgLoader path="/icons/content-spinner.svg" />
        </span>
      )}
    </Element>
  );
}

TextLink.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  icon: PropTypes.string,
  to: PropTypes.string,
  onClick: PropTypes.func,
  data: PropTypes.object, // @todo prop name too generic. rename into trackingData
  size: PropTypes.oneOf([
    'secondary',
    'small',
  ]),
  element: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element,
  ]),
  color: PropTypes.oneOf([
    'dark',
    'light',
  ]),
  asButton: PropTypes.oneOfType([
    PropTypes.oneOf([
      'S',
      'SM',
      'M',
      'ML',
      'L',
    ]),
    PropTypes.bool,
  ]),
  withArrow: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  withoutLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  utilities: PropTypes.array,
  target: PropTypes.oneOf(['_blank', '_self']),
  isActive: PropTypes.bool,
  isDisabled: PropTypes.bool,
  buttonFilled: PropTypes.bool,
  tertiary: PropTypes.bool,
  highlight: PropTypes.bool,
  withoutArrow: PropTypes.bool,
  withoutStyle: PropTypes.bool,
  buttonWithoutLabel: PropTypes.bool,
  href: PropTypes.string,
  asShareButton: PropTypes.bool,
  asShareButtonInverted: PropTypes.bool,
  standalone: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  forceSpaceTrigger: PropTypes.bool,
  ...ariaPropsTypes,
};

TextLink.defaultProps = {
  to: '/',
  data: {},
  isLoggedIn: false,
  onClick: null,
  standalone: false,
};

export default TextLink;
